import { z } from 'zod';

/**
 * Org profile edit (Settings › Hồ sơ › Tổ chức), gated to `manage:settings`.
 * Both fields are optional so the UI can save the name and the logo
 * independently, but an empty body is a client bug rather than a no-op.
 */
export const updateOrgSchema = z
  .object({
    name: z.string().trim().min(1, 'Organization name is required').max(120, 'Name is too long').optional(),
    // `.url()` alone accepts any URI with a scheme (including `javascript:`,
    // per the WHATWG URL parser `new URL('javascript:...')` succeeds) — restrict
    // to http/https so a logo can never carry an executable/script scheme.
    logoUrl: z
      .string()
      .url('Logo URL must be a valid URL')
      .refine((v) => /^https?:\/\//i.test(v), 'Logo URL must use http or https')
      .optional(),
    // THI-263: `#rrggbb` (any case, stored lowercase by the route); null clears
    // back to the LopHoc default. Omitted = untouched.
    brandColor: z
      .string()
      .trim()
      .regex(/^#[0-9a-fA-F]{6}$/, 'Brand colour must be #rrggbb')
      .nullable()
      .optional(),
  })
  .refine((v) => v.name !== undefined || v.logoUrl !== undefined || v.brandColor !== undefined, {
    message: 'At least one field is required',
  });

export type UpdateOrgInput = z.infer<typeof updateOrgSchema>;

/**
 * Org settings edit (Settings › excused-absence quota, manual-send message
 * template, invoice watermark), gated to `manage:settings`. `null` clears a
 * field (no cap / built-in default template); an omitted field is left
 * untouched by the route's partial-update semantics — see PUT /org/settings.
 */
export const updateOrgSettingsSchema = z.object({
  maxExcusedPerMonth: z.number().int().min(0).nullable().optional(),
  // Manual-send message template (THI-87): raw text with `{token}` markers,
  // resolved to a per-invoice message client-side (invoiceMessage.ts). `null`
  // = use the built-in default. 2000-char cap matches the design spec.
  invoiceMessageTemplate: z.string().trim().max(2000).nullable().optional(),
  // Watermark toggle (THI-90): the org's own preference for the public-invoice
  // badge. Not nullable — unlike the two fields above, there is no "unset"
  // state to clear back to; the column default (true) already covers that.
  // Whether it actually shows also depends on the plan — see `showsWatermark`
  // in the public invoice route — this field only stores the org's ask.
  showInvoiceWatermark: z.boolean().optional(),
  // Default invoice due-day (THI-100): day of month 1–28 (every month has one;
  // "the 30th" is not — pick 28 or override per chốt-sổ run). `null` clears
  // back to the +7-days rule; omitted is left untouched by the route's
  // partial-update semantics, same as the three fields above.
  invoiceDueDay: z.number().int().min(1).max(28).nullable().optional(),
  // THI-267: COHORT vs INDIVIDUAL (see OrganizationSettings.classMode). Not
  // nullable — same reason as showInvoiceWatermark above, no "unset" state.
  classMode: z.enum(['COHORT', 'INDIVIDUAL']).optional(),
});

export type UpdateOrgSettingsInput = z.infer<typeof updateOrgSettingsSchema>;
